import { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

// ─── helpers ────────────────────────────────────────────────────────────────

const today = () => { const d = new Date(); d.setHours(0,0,0,0); return d }

function fmtDate(str) {
  if (!str) return null
  const d = new Date(str + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const STATUS_PILL = {
  'In Work':   'bg-green-100 text-green-700',
  'Scheduled': 'bg-gray-100 text-gray-600',
  'TBD':       'bg-yellow-100 text-yellow-700',
}

function ProjectRow({ p }) {
  const reg   = p.aircraft?.registration || p.secondary_registration || '—'
  const start = fmtDate(p.arrival_date)
  const end   = fmtDate(p.departure_date)
  const dates = start && end ? `${start} – ${end}` : start || end || null
  return (
    <span className="flex flex-col gap-1 py-0.5">
      <span className="flex items-baseline gap-1.5">
        <span className="font-semibold text-gray-900 text-sm tracking-wide">{reg}</span>
        {p.workscope && <span className="text-xs text-gray-400 font-normal">— {p.workscope}</span>}
      </span>
      <span className="flex flex-wrap gap-1">
        {p.work_order && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 text-brand-red">{p.work_order}</span>
        )}
        {dates && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">{dates}</span>
        )}
        {p.status && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_PILL[p.status] ?? 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
        )}
      </span>
    </span>
  )
}

function projectPlainLabel(p) {
  const reg   = p.aircraft?.registration || p.secondary_registration || '—'
  const start = fmtDate(p.arrival_date)
  const end   = fmtDate(p.departure_date)
  const dates = start && end ? `${start} – ${end}` : start || end || ''
  return [reg, p.work_order, dates, p.status].filter(Boolean).join(' · ')
}

function ProjectPicker({ value, onChange, projects }) {
  const [search, setSearch] = useState('')

  const filtered = projects.filter(p => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    const reg = (p.aircraft?.registration || p.secondary_registration || '').toLowerCase()
    return reg.includes(q)
      || (p.work_order || '').toLowerCase().includes(q)
      || (p.workscope || '').toLowerCase().includes(q)
      || (p.status || '').toLowerCase().includes(q)
  })

  const selected = projects.find(p => p.id === value)

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by registration or work order…"
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-7 focus:outline-none focus:border-brand-red"
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-base leading-none">&times;</button>
        )}
      </div>
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
        <button
          type="button"
          onClick={() => onChange('')}
          className={`w-full text-left text-sm px-3 py-2 hover:bg-gray-50 transition-colors ${!value ? 'bg-red-50 text-brand-red font-medium' : 'text-gray-400'}`}
        >
          — None —
        </button>
        {filtered.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${value === p.id ? 'bg-red-50' : ''}`}
          >
            <ProjectRow p={p} />
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-gray-400 px-3 py-2 italic">No matches</p>
        )}
      </div>
      {selected && (
        <p className="text-xs text-brand-red">✓ {projectPlainLabel(selected)}</p>
      )}
    </div>
  )
}

function dueInfo(dateStr) {
  if (!dateStr) return null
  const due  = new Date(dateStr + 'T00:00:00')
  const diff = Math.floor((due - today()) / 86400000)
  if (diff < 0)  return { text: `${Math.abs(diff)}d overdue`, cls: 'text-red-600 font-medium' }
  if (diff === 0) return { text: 'Due today',    cls: 'text-orange-600 font-medium' }
  if (diff === 1) return { text: 'Due tomorrow', cls: 'text-yellow-600' }
  return { text: due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), cls: 'text-gray-500' }
}

function isTaskDone(assigneeStatus, needsApproval) {
  return assigneeStatus === 'approved' || (!needsApproval && assigneeStatus === 'complete')
}

const PRIORITY_CLS = {
  high:   'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low:    'bg-green-100 text-green-700',
}

const STATUS_CLS = {
  open:      'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-100 text-blue-700',
  complete:  'bg-green-100 text-green-700',
  approved:  'bg-emerald-100 text-emerald-700',
  rejected:  'bg-red-100 text-red-700',
}

const STATUS_LABEL = {
  open:      'Open',
  submitted: 'Pending Approval',
  complete:  'Complete',
  approved:  'Approved',
  rejected:  'Rejected',
}

function Badge({ text, cls }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{text}</span>
}

// ─── recurring expansion ─────────────────────────────────────────────────────

function expandRecurringForCalendar(tasks) {
  const horizon = new Date()
  horizon.setMonth(horizon.getMonth() + 3)
  const todayMs = new Date().setHours(0, 0, 0, 0)

  const result = []
  for (const t of tasks) {
    result.push(t)
    if (!t.is_recurring || t.needs_approval) continue
    if (!t.due_date || !t.recurrence_rule) continue

    const cursor = new Date(t.due_date + 'T00:00:00')
    for (let i = 0; i < 200; i++) {
      if (t.recurrence_rule === 'daily')        cursor.setDate(cursor.getDate() + 1)
      else if (t.recurrence_rule === 'weekly')  cursor.setDate(cursor.getDate() + 7)
      else if (t.recurrence_rule === 'monthly') cursor.setMonth(cursor.getMonth() + 1)
      if (cursor > horizon) break
      if (cursor.getTime() < todayMs) continue
      const ds = cursor.toISOString().slice(0, 10)
      result.push({ ...t, due_date: ds, id: `${t.id}__${ds}`, _taskId: t.id, _isRecurringInstance: true })
    }
  }
  return result
}

// ─── data layer ─────────────────────────────────────────────────────────────

async function fetchMyTasks(userId) {
  const { data: rows } = await supabase
    .from('task_assignees')
    .select('task_id, status, rejection_note, submitted_at, approved_at')
    .eq('user_id', userId)
  if (!rows?.length) return []

  const taskIds = rows.map(r => r.task_id)
  const myMap   = Object.fromEntries(rows.map(r => [r.task_id, r]))

  const { data: tasks } = await supabase
    .from('tasks')
    .select('*, task_assignees(user_id, status), task_comments(id), task_watchers(user_id)')
    .in('id', taskIds)
    .is('deleted_at', null)
  if (!tasks) return []

  return tasks.map(t => ({ ...t, myRow: myMap[t.id], commentCount: t.task_comments?.length ?? 0 }))
}

async function fetchAssignedTasks(userId) {
  const { data } = await supabase
    .from('tasks')
    .select('*, task_assignees(user_id, status), task_comments(id)')
    .eq('created_by', userId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
  return data ?? []
}

async function fetchUnreadCommentTaskIds(userId) {
  const { data } = await supabase
    .from('task_notifications')
    .select('task_id')
    .eq('user_id', userId)
    .eq('type', 'comment')
    .is('seen_at', null)
  return new Set((data || []).map(r => r.task_id))
}


async function fetchTaskDetail(taskId) {
  const { data: task } = await supabase
    .from('tasks')
    .select('*')
    .eq('id', taskId)
    .single()
  if (!task) return null

  const [{ data: assignees }, { data: comments }, { data: watchers }] = await Promise.all([
    supabase.from('task_assignees').select('user_id, status, rejection_note, submitted_at, approved_at').eq('task_id', taskId),
    supabase.from('task_comments').select('id, user_id, body, created_at').eq('task_id', taskId).order('created_at'),
    supabase.from('task_watchers').select('user_id').eq('task_id', taskId),
  ])

  // collect all user ids to fetch profiles once
  const uids = [...new Set([
    task.created_by,
    ...(assignees || []).map(a => a.user_id),
    ...(comments  || []).map(c => c.user_id),
    ...(watchers  || []).map(w => w.user_id),
  ])]
  const { data: profileRows } = await supabase
    .from('profiles')
    .select('id, display_name, email')
    .in('id', uids)
  const profileMap = Object.fromEntries((profileRows || []).map(p => [p.id, p]))

  let projectReg = null
  if (task.linked_project_id) {
    const { data: proj } = await supabase
      .from('projects')
      .select('secondary_registration, aircraft(registration)')
      .eq('id', task.linked_project_id)
      .single()
    if (proj) projectReg = proj.aircraft?.registration || proj.secondary_registration || null
  }

  return {
    ...task,
    assignees: (assignees || []).map(a => ({ ...a, profile: profileMap[a.user_id] })),
    comments:  (comments  || []).map(c => ({ ...c, profile: profileMap[c.user_id] })),
    watchers:  (watchers  || []).map(w => ({ ...w, profile: profileMap[w.user_id] })),
    creatorProfile: profileMap[task.created_by],
    projectReg,
  }
}

async function insertNotifications(userIds, taskId, type) {
  if (!userIds.length) return
  const rows = userIds.map(uid => ({ user_id: uid, task_id: taskId, type }))
  await supabase.from('task_notifications').insert(rows)
}

// ─── sub-components ──────────────────────────────────────────────────────────

function TaskCard({ task, myRow, onClick, hasUnread, creatorName, reg }) {
  const due     = dueInfo(task.due_date)
  const isDone  = myRow ? isTaskDone(myRow.status, task.needs_approval) : false
  const status  = myRow?.status ?? 'open'

  return (
    <div
      onClick={onClick}
      className={`border rounded-lg bg-white hover:border-brand-red/30 hover:shadow-sm transition-all cursor-pointer px-4 py-3 ${isDone ? 'opacity-60' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${isDone ? 'line-through text-gray-400' : 'text-gray-900'}`}>{task.title}</span>
            <Badge text={task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} cls={PRIORITY_CLS[task.priority] ?? PRIORITY_CLS.medium} />
            {myRow && <Badge text={STATUS_LABEL[status] ?? status} cls={STATUS_CLS[status] ?? STATUS_CLS.open} />}
            {hasUnread && <Badge text="New Message" cls="bg-red-50 text-brand-red" />}
            {task.needs_approval && <span className="text-xs text-gray-400">· Needs approval</span>}
          </div>
          {myRow?.rejection_note && myRow.status === 'rejected' && (
            <p className="text-xs text-red-600">Rejected: {myRow.rejection_note}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {reg && <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded text-xs">{reg}</span>}
            {due && !isDone && <span className={due.cls}>{due.text}</span>}
            {creatorName && <span>by {creatorName}</span>}
            {task.linked_wo && <span>WO: {task.linked_wo}</span>}
            {(task.task_comments?.length > 0) && (
              <span className={`flex items-center gap-1 ${hasUnread ? 'text-red-500 font-medium' : ''}`}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {task.task_comments.length}
              </span>
            )}
          </div>
        </div>
        <svg className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  )
}

function AssignedTaskCard({ task, onClick, hasUnread, profileMap, reg }) {
  const assignees    = task.task_assignees ?? []
  const total        = assignees.length
  const submitted    = assignees.filter(a => a.status === 'submitted').length
  const done         = assignees.filter(a => a.status === 'approved' || a.status === 'complete').length
  const due          = dueInfo(task.due_date)

  const assigneeNames = assignees
    .map(a => profileMap?.[a.user_id]?.display_name || profileMap?.[a.user_id]?.email || '?')
    .join(', ')

  let summaryText, summaryCls
  if (submitted > 0) {
    summaryText = `${submitted} pending approval`
    summaryCls  = 'text-brand-red'
  } else if (done === total && total > 0) {
    summaryText = 'All complete'
    summaryCls  = 'text-emerald-600'
  } else {
    summaryText = `${done}/${total} done`
    summaryCls  = 'text-gray-500'
  }

  return (
    <div
      onClick={onClick}
      className="border border-gray-200 rounded-lg bg-white hover:border-brand-red/30 hover:shadow-sm transition-all cursor-pointer px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{task.title}</span>
            <Badge text={task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} cls={PRIORITY_CLS[task.priority] ?? PRIORITY_CLS.medium} />
            {hasUnread && <Badge text="New Message" cls="bg-red-50 text-brand-red" />}
          </div>
          <div className="flex items-center gap-3 text-xs">
            {reg && <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded text-xs shrink-0">{reg}</span>}
            {assigneeNames && <span className="text-gray-500 truncate">To: {assigneeNames}</span>}
            <span className={`shrink-0 ${summaryCls}`}>{summaryText}</span>
            {due && !(done === total && total > 0) && <span className={`shrink-0 ${due.cls}`}>{due.text}</span>}
            {task.linked_wo && <span className="shrink-0 text-gray-500">WO: {task.linked_wo}</span>}
          </div>
        </div>
        <svg className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  )
}

function MonthCalendar({ tasks, selectedDate, onSelectDate }) {
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d
  })

  const year  = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const todayStr = new Date().toISOString().slice(0, 10)
  const pad = n => String(n).padStart(2, '0')

  const tasksByDate = useMemo(() => {
    const map = {}
    for (const t of tasks) {
      if (!t.due_date) continue
      if (!map[t.due_date]) map[t.due_date] = []
      map[t.due_date].push(t)
    }
    return map
  }, [tasks])

  const startOffset = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Month nav */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <span className="text-sm font-semibold text-gray-900">
          {viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-100">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-1.5">{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 border-l border-t border-gray-100">
        {cells.map((day, i) => {
          if (!day) return <div key={`p${i}`} className="border-b border-r border-gray-100 min-h-[64px] bg-gray-50/50" />
          const ds = `${year}-${pad(month + 1)}-${pad(day)}`
          const dayTasks = tasksByDate[ds] || []
          const isToday    = ds === todayStr
          const isPast     = ds < todayStr
          const isSelected = ds === selectedDate

          return (
            <div
              key={ds}
              onClick={() => dayTasks.length && onSelectDate(isSelected ? null : ds)}
              className={`border-b border-r border-gray-100 min-h-[64px] p-1.5 transition-colors ${
                dayTasks.length ? 'cursor-pointer' : ''
              } ${isSelected ? 'bg-red-50' : dayTasks.length ? 'hover:bg-gray-50' : ''}`}
            >
              <div className={`w-6 h-6 flex items-center justify-center text-xs font-semibold rounded-full mb-0.5 ${
                isToday ? 'bg-brand-red text-white' :
                isPast && dayTasks.length ? 'text-red-500' :
                'text-gray-600'
              }`}>
                {day}
              </div>
              <div className="space-y-0.5">
                {dayTasks.slice(0, 2).map(t => (
                  <div key={t.id} className={`truncate text-xs px-1 py-0.5 rounded leading-tight ${
                    t._isDone   ? 'bg-gray-50 text-gray-400 line-through' :
                    isPast      ? 'bg-red-50 text-red-700' :
                    isToday     ? 'bg-orange-50 text-orange-700' :
                                  'bg-red-50 text-brand-red'
                  }`}>
                    {t.title}
                  </div>
                ))}
                {dayTasks.length > 2 && (
                  <div className="text-xs text-gray-400 px-1">+{dayTasks.length - 2} more</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TrainingAlertCard({ alert, onNavigate }) {
  const isUrgent = alert.kind === 'overdue' || alert.kind === 'due-today'
  const label = alert.kind === 'overdue'    ? (alert.days != null ? `${Math.abs(alert.days)}d overdue` : 'Overdue')
              : alert.kind === 'due-today'  ? 'Due today'
              : alert.kind === 'due-soon'   ? `Due in ${alert.days}d`
              : 'Not yet completed'
  return (
    <div className={`border rounded-lg px-4 py-3 flex items-center gap-3 ${isUrgent ? 'border-red-200 bg-red-50/40' : alert.kind === 'due-soon' ? 'border-yellow-200 bg-yellow-50/40' : 'border-gray-200 bg-white'}`}>
      <svg className={`w-4 h-4 shrink-0 ${isUrgent ? 'text-red-500' : alert.kind === 'due-soon' ? 'text-yellow-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      </svg>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 truncate block">{alert.title}</span>
        <span className={`text-xs font-semibold ${isUrgent ? 'text-red-600' : alert.kind === 'due-soon' ? 'text-yellow-700' : 'text-gray-500'}`}>{label}</span>
      </div>
      {onNavigate && (
        <button onClick={onNavigate} className="text-xs text-brand-red hover:text-brand-red-dark shrink-0 font-medium">View →</button>
      )}
    </div>
  )
}

function SectionGroup({ label, count, children, defaultOpen = true, accentCls = '' }) {
  const [open, setOpen] = useState(defaultOpen)
  if (count === 0) return null
  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 py-1.5 text-left group"
      >
        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className={`text-xs font-semibold uppercase tracking-wide ${accentCls || 'text-gray-500'}`}>{label}</span>
        <span className="text-xs text-gray-400 ml-1">{count}</span>
      </button>
      {open && <div className="space-y-2 mt-1">{children}</div>}
    </div>
  )
}

// ─── Task Detail Panel ───────────────────────────────────────────────────────

function TaskDetailPanel({ taskId, currentUserId, isCreator: creatorFromProp, onClose, onRefresh, allProjects, assignableProfiles }) {
  const { isEditor } = useAuth()
  const [task, setTask]           = useState(null)
  const [loading, setLoading]     = useState(true)
  const [comment, setComment]     = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [rejectingUid, setRejectingUid] = useState(null)
  const [rejectNote, setRejectNote]     = useState('')
  const [editing, setEditing]               = useState(false)
  const [editForm, setEditForm]             = useState({})
  const [editNewAssigneeIds, setEditNewAssigneeIds] = useState([])
  const [showDeleteConfirm, setShowDeleteConfirm]   = useState(false)

  const load = async () => {
    setLoading(true)
    const detail = await fetchTaskDetail(taskId)
    setTask(detail)
    setLoading(false)
  }

  useEffect(() => { load() }, [taskId])

  const myAssignee  = task?.assignees?.find(a => a.user_id === currentUserId)
  const isCreator   = task?.created_by === currentUserId
  const canComment  = !!task
  const allApproved = task?.assignees?.length > 0 && task.assignees.every(a => a.status === 'approved' || a.status === 'complete')
  const canEdit = !myAssignee && (isEditor || (isCreator && !allApproved))
  const canDelete = isCreator

  const startEdit = () => {
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      due_date: task.due_date || '',
      linked_project_id: task.linked_project_id || '',
      is_recurring: task.is_recurring || false,
      recurrence_rule: task.recurrence_rule || 'weekly',
    })
    setEditNewAssigneeIds([])
    setEditing(true)
  }

  const handleSave = async () => {
    if (!editForm.title.trim()) return
    setSubmitting(true)
    try {
      await supabase.from('tasks').update({
        title:             editForm.title.trim(),
        description:       editForm.description.trim() || null,
        priority:          editForm.priority,
        due_date:          editForm.due_date || null,
        linked_project_id: editForm.linked_project_id || null,
        is_recurring:      editForm.is_recurring,
        recurrence_rule:   editForm.is_recurring ? editForm.recurrence_rule : null,
      }).eq('id', taskId)
      if (editNewAssigneeIds.length) {
        await supabase.from('task_assignees').insert(
          editNewAssigneeIds.map(uid => ({ task_id: taskId, user_id: uid, status: 'open' }))
        )
        await insertNotifications(editNewAssigneeIds, taskId, 'assigned')
      }
      setEditing(false)
      await load()
      onRefresh?.()
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (mode) => {
    setSubmitting(true)
    try {
      if (mode === 'series' || !task.is_recurring) {
        await supabase.rpc('soft_delete_task', { p_task_id: taskId })
        onClose()
        onRefresh?.()
      } else {
        // 'instance' — advance due_date to next occurrence
        const cursor = new Date((task.due_date || new Date().toISOString().slice(0, 10)) + 'T00:00:00')
        const rule = task.recurrence_rule || 'weekly'
        if (rule === 'daily')        cursor.setDate(cursor.getDate() + 1)
        else if (rule === 'weekly')  cursor.setDate(cursor.getDate() + 7)
        else if (rule === 'monthly') cursor.setMonth(cursor.getMonth() + 1)
        await supabase.from('tasks').update({ due_date: cursor.toISOString().slice(0, 10) }).eq('id', taskId)
        setShowDeleteConfirm(false)
        await load()
        onRefresh?.()
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleAction = async (action, targetUid, note) => {
    setSubmitting(true)
    try {
      if (action === 'complete' || action === 'submit') {
        if (action === 'complete' && task.is_recurring) {
          // Use a security-definer RPC so any assignee (including viewers) can advance the series
          await supabase.rpc('advance_recurring_task', { p_task_id: taskId })
          onRefresh?.()
          onClose()
          return
        }
        const newStatus = action === 'submit' ? 'submitted' : 'complete'
        await supabase.from('task_assignees')
          .update({ status: newStatus, submitted_at: new Date().toISOString() })
          .eq('task_id', taskId).eq('user_id', currentUserId)

        const notifyType = action === 'submit' ? 'submitted' : 'completed_no_approval'
        await insertNotifications([task.created_by], taskId, notifyType)

      } else if (action === 'approve') {
        await supabase.from('task_assignees')
          .update({ status: 'approved', approved_at: new Date().toISOString() })
          .eq('task_id', taskId).eq('user_id', targetUid)
        await insertNotifications([targetUid], taskId, 'approved')

      } else if (action === 'reject') {
        await supabase.from('task_assignees')
          .update({ status: 'rejected', rejection_note: note })
          .eq('task_id', taskId).eq('user_id', targetUid)
        await insertNotifications([targetUid], taskId, 'rejected')
        setRejectingUid(null)
        setRejectNote('')
      }
      await load()
      onRefresh?.()
    } finally {
      setSubmitting(false)
    }
  }

  const handleComment = async () => {
    if (!comment.trim()) return
    setSubmitting(true)
    const body = comment.trim()
    setComment('')
    const allUids = [
      task.created_by,
      ...(task.assignees || []).map(a => a.user_id),
      ...(task.watchers  || []).map(w => w.user_id),
    ].filter((uid, i, arr) => uid !== currentUserId && arr.indexOf(uid) === i)

    await supabase.from('task_comments').insert({ task_id: taskId, user_id: currentUserId, body })
    await insertNotifications(allUids, taskId, 'comment')
    setSubmitting(false)
    await load()
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Delete confirm overlay */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-10 bg-white/95 flex flex-col items-center justify-center gap-4 px-8">
            <p className="text-base font-semibold text-gray-900 text-center">
              {task?.is_recurring ? 'Delete recurring task?' : 'Delete this task?'}
            </p>
            <p className="text-sm text-gray-500 text-center">
              {task?.is_recurring
                ? 'Do you want to remove just this occurrence (advances to the next due date) or delete the entire series?'
                : 'This cannot be undone.'}
            </p>
            <div className="flex flex-col gap-2 w-full max-w-xs">
              {task?.is_recurring && (
                <button
                  onClick={() => handleDelete('instance')}
                  disabled={submitting}
                  className="w-full text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg px-4 py-2.5 disabled:opacity-40 transition-colors"
                >
                  Skip this occurrence
                </button>
              )}
              <button
                onClick={() => handleDelete('series')}
                disabled={submitting}
                className="w-full text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg px-4 py-2.5 disabled:opacity-40 transition-colors"
              >
                {task?.is_recurring ? 'Delete entire series' : 'Delete task'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 truncate pr-4">
            {loading ? 'Loading…' : (editing ? 'Edit Task' : task?.title)}
          </h2>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!loading && task && canEdit && !editing && (
              <button onClick={startEdit} className="text-xs font-medium text-brand-red hover:text-brand-red-dark border border-red-200 rounded px-2 py-1 transition-colors">Edit</button>
            )}
            {!loading && task && canDelete && !editing && (
              <button onClick={() => setShowDeleteConfirm(true)} className="text-xs font-medium text-red-500 hover:text-red-700 border border-red-200 rounded px-2 py-1 transition-colors">Delete</button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : !task ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Task not found.</div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Meta */}
            <div className="px-5 py-4 space-y-3 border-b border-gray-100">
              {editing ? (
                <>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Title</label>
                      <input
                        value={editForm.title}
                        onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red"
                      />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Priority</label>
                        <select
                          value={editForm.priority}
                          onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red"
                        >
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Due Date</label>
                        <input
                          type="date"
                          value={editForm.due_date}
                          onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red"
                        />
                      </div>
                    </div>
                    {allProjects?.length > 0 && (
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Project</label>
                        <ProjectPicker
                          value={editForm.linked_project_id}
                          onChange={v => setEditForm(f => ({ ...f, linked_project_id: v }))}
                          projects={allProjects}
                        />
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Description</label>
                      <textarea
                        value={editForm.description}
                        onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                        rows={3}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red resize-none"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.is_recurring}
                          onChange={e => setEditForm(f => ({ ...f, is_recurring: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">Recurring</span>
                      </label>
                      {editForm.is_recurring && (
                        <select
                          value={editForm.recurrence_rule}
                          onChange={e => setEditForm(f => ({ ...f, recurrence_rule: e.target.value }))}
                          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-brand-red"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      )}
                    </div>
                    {assignableProfiles?.length > 0 && (
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Add Assignees</label>
                        <div className="space-y-1 max-h-36 overflow-y-auto border border-gray-200 rounded-lg px-2 py-1">
                          {assignableProfiles
                            .filter(p => !(task.assignees || []).some(a => a.user_id === p.id))
                            .map(p => (
                              <label key={p.id} className="flex items-center gap-2 py-0.5 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                                <input
                                  type="checkbox"
                                  checked={editNewAssigneeIds.includes(p.id)}
                                  onChange={() => setEditNewAssigneeIds(prev =>
                                    prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                                  )}
                                  className="rounded"
                                />
                                {p.display_name || p.email}
                              </label>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSave}
                      disabled={!editForm.title?.trim() || submitting}
                      className="text-sm font-medium text-white bg-brand-red hover:bg-brand-red-dark rounded-lg px-4 py-1.5 disabled:opacity-40 transition-colors"
                    >Save</button>
                    <button
                      onClick={() => setEditing(false)}
                      className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 transition-colors"
                    >Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    const allApproved = task.assignees?.length > 0 && task.assignees.every(a => a.status === 'approved' || a.status === 'complete')
                    return (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge text={task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} cls={PRIORITY_CLS[task.priority] ?? PRIORITY_CLS.medium} />
                        {task.due_date && !allApproved && (() => { const d = dueInfo(task.due_date); return <span className={`text-sm ${d.cls}`}>{d.text}</span> })()}
                        {task.needs_approval && (
                          allApproved
                            ? <Badge text="Approved" cls={STATUS_CLS.approved} />
                            : <span className="text-xs text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">Needs approval</span>
                        )}
                        {task.is_recurring && <span className="text-xs text-purple-600 border border-purple-200 rounded px-1.5 py-0.5">Recurring</span>}
                      </div>
                    )
                  })()}
                  {task.description && <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description}</p>}
                  {task.linked_wo && <p className="text-xs text-gray-500">Work Order: <span className="font-medium text-brand-red">{task.linked_wo}</span></p>}
                  {task.projectReg && (
                    <div><span className="font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-sm">{task.projectReg}</span></div>
                  )}
                  <p className="text-xs text-gray-400">Created by {task.creatorProfile?.display_name || task.creatorProfile?.email || '—'}</p>
                </>
              )}
            </div>

            {/* Assignees */}
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assignees</p>
              <div className="space-y-2">
                {(task.assignees || []).map(a => (
                  <div key={a.user_id} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                      {(a.profile?.display_name || a.profile?.email || '?')[0].toUpperCase()}
                    </div>
                    <span className="text-sm text-gray-700 flex-1">{a.profile?.display_name || a.profile?.email}</span>
                    <Badge text={STATUS_LABEL[a.status] ?? a.status} cls={STATUS_CLS[a.status] ?? STATUS_CLS.open} />

                    {/* Approve / Reject buttons for creator on submitted tasks */}
                    {isCreator && a.status === 'submitted' && task.needs_approval && (
                      rejectingUid === a.user_id ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={rejectNote}
                            onChange={e => setRejectNote(e.target.value)}
                            placeholder="Reason…"
                            className="text-xs border border-gray-200 rounded px-2 py-1 w-32 focus:outline-none focus:border-brand-red"
                          />
                          <button
                            onClick={() => handleAction('reject', a.user_id, rejectNote)}
                            disabled={!rejectNote.trim() || submitting}
                            className="text-xs text-white bg-red-500 hover:bg-red-600 rounded px-2 py-1 disabled:opacity-40"
                          >Send</button>
                          <button onClick={() => setRejectingUid(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleAction('approve', a.user_id)}
                            disabled={submitting}
                            className="text-xs text-white bg-emerald-500 hover:bg-emerald-600 rounded px-2 py-1 disabled:opacity-40"
                          >Approve</button>
                          <button
                            onClick={() => { setRejectingUid(a.user_id); setRejectNote('') }}
                            className="text-xs text-white bg-red-400 hover:bg-red-500 rounded px-2 py-1"
                          >Reject</button>
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>

              {/* My action button */}
              {myAssignee && !isTaskDone(myAssignee.status, task.needs_approval) && (
                <div className="mt-3">
                  {myAssignee.status === 'open' || myAssignee.status === 'rejected' ? (
                    <button
                      onClick={() => handleAction(task.needs_approval ? 'submit' : 'complete')}
                      disabled={submitting}
                      className="text-sm text-white bg-brand-red hover:bg-brand-red-dark rounded-lg px-4 py-2 disabled:opacity-40 transition-colors"
                    >
                      {task.needs_approval ? 'Mark Complete & Submit for Approval' : 'Mark Complete'}
                    </button>
                  ) : myAssignee.status === 'submitted' ? (
                    <p className="text-sm text-brand-red">Waiting for approval…</p>
                  ) : null}
                </div>
              )}
              {myAssignee && isTaskDone(myAssignee.status, task.needs_approval) && (
                <p className="mt-2 text-sm text-emerald-600 font-medium">✓ Your portion is complete</p>
              )}
            </div>

            {/* Watchers */}
            {(task.watchers || []).length > 0 && (
              <div className="px-5 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">CC'd</p>
                <div className="flex flex-wrap gap-1">
                  {task.watchers.map(w => (
                    <span key={w.user_id} className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                      {w.profile?.display_name || w.profile?.email}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Comments */}
            <div className="px-5 py-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Comments</p>
              {(task.comments || []).length === 0 ? (
                <p className="text-sm text-gray-400">No comments yet.</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {task.comments.map(c => (
                    <div key={c.id} className="flex gap-2.5">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0 mt-0.5">
                        {(c.profile?.display_name || c.profile?.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-medium text-gray-700">{c.profile?.display_name || c.profile?.email}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{c.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {canComment && (
                <div className="flex gap-2">
                  <textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleComment() }}
                    placeholder="Add a comment… (Ctrl+Enter to send)"
                    rows={2}
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red resize-none"
                  />
                  <button
                    onClick={handleComment}
                    disabled={!comment.trim() || submitting}
                    className="self-end text-sm text-white bg-brand-red hover:bg-brand-red-dark rounded-lg px-3 py-2 disabled:opacity-40 transition-colors"
                  >Send</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Create Task Modal ───────────────────────────────────────────────────────

function TaskCreateModal({ currentUserId, allProfiles, assignableProfiles, allProjects, onClose, onCreated, selfAssign = false }) {
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', due_date: '',
    needs_approval: selfAssign ? false : true, is_recurring: false, recurrence_rule: 'weekly',
    linked_project_id: '',
  })
  const [assigneeIds, setAssigneeIds]   = useState(selfAssign ? [currentUserId] : [])
  const [watcherIds,  setWatcherIds]    = useState([])
  const [submitting,  setSubmitting]    = useState(false)
  const [error,       setError]         = useState(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const toggleAssignee = id => setAssigneeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const toggleWatcher  = id => setWatcherIds(prev =>  prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleSubmit = async () => {
    if (!form.title.trim()) return setError('Title is required.')
    if (!assigneeIds.length) return setError('Assign to at least one person.')
    setSubmitting(true)
    setError(null)
    try {
      const taskId = crypto.randomUUID()
      const taskPayload = {
        id:               taskId,
        title:            form.title.trim(),
        description:      form.description.trim() || null,
        priority:         form.priority,
        due_date:         form.due_date || null,
        needs_approval:   form.needs_approval,
        is_recurring:     form.is_recurring,
        recurrence_rule:  form.is_recurring ? form.recurrence_rule : null,
        linked_project_id: form.linked_project_id || null,
        source:           'manual',
      }
      const { error: taskErr } = await supabase.from('tasks').insert(taskPayload)
      if (taskErr) throw taskErr

      if (assigneeIds.length) {
        await supabase.from('task_assignees').insert(
          assigneeIds.map(uid => ({ task_id: taskId, user_id: uid, status: 'open' }))
        )
      }
      if (watcherIds.length) {
        await supabase.from('task_watchers').insert(
          watcherIds.map(uid => ({ task_id: taskId, user_id: uid }))
        )
      }
      // Notify assignees (skip when self-assigning — no need to notify yourself)
      if (!selfAssign) await insertNotifications(assigneeIds, taskId, 'assigned')

      onCreated?.()
      onClose()
    } catch (e) {
      setError(e.message || 'Failed to create task.')
    } finally {
      setSubmitting(false)
    }
  }

  const allOtherProfiles = allProfiles.filter(p => p.id !== currentUserId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-gray-900">{selfAssign ? 'Add To-Do' : 'Create Task'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="Task title"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={3}
              placeholder="Optional details…"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red resize-none"
            />
          </div>

          {/* Priority + Due date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={e => set('priority', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-brand-red bg-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={e => set('due_date', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-brand-red"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            {!selfAssign && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.needs_approval} onChange={e => set('needs_approval', e.target.checked)} className="rounded" />
                <span className="text-sm text-gray-700">Needs approval</span>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_recurring} onChange={e => set('is_recurring', e.target.checked)} className="rounded" />
              <span className="text-sm text-gray-700">Recurring</span>
            </label>
          </div>

          {form.is_recurring && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Recurrence</label>
              <select
                value={form.recurrence_rule}
                onChange={e => set('recurrence_rule', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-brand-red bg-white"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          )}

          {/* Link to Project */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Link to Project (optional)</label>
            <ProjectPicker
              value={form.linked_project_id}
              onChange={v => set('linked_project_id', v)}
              projects={allProjects}
            />
          </div>

          {/* Assignees — hidden for self-assigned to-dos */}
          {!selfAssign && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Assign To <span className="text-red-500">*</span></label>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                {assignableProfiles.map(p => (
                  <label key={p.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={assigneeIds.includes(p.id)}
                      onChange={() => toggleAssignee(p.id)}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700">{p.display_name || p.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Watchers / CC — hidden for self-assigned to-dos */}
          {!selfAssign && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">CC (optional)</label>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-32 overflow-y-auto">
                {allOtherProfiles.filter(p => !assigneeIds.includes(p.id)).map(p => (
                  <label key={p.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={watcherIds.includes(p.id)}
                      onChange={() => toggleWatcher(p.id)}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700">{p.display_name || p.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="text-sm font-medium text-white bg-brand-red hover:bg-brand-red-dark rounded-lg px-5 py-2 disabled:opacity-40 transition-colors"
          >
            {submitting ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function MyTasksTab({ refreshKey = 0, onNavigateToTraining }) {
  const { user, profile, isEditor, isSubtaskEditor } = useAuth()
  const canAssign = isEditor || isSubtaskEditor

  const [activeTab,      setActiveTab]      = useState('mine')
  const [myTasks,        setMyTasks]        = useState([])
  const [assignedTasks,  setAssignedTasks]  = useState([])
  const [loading,        setLoading]        = useState(true)
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const [showCreate,     setShowCreate]     = useState(false)
  const [showSelfCreate, setShowSelfCreate] = useState(false)
  const [allProfiles,    setAllProfiles]    = useState([])
  const [allProjects,    setAllProjects]    = useState([])
  const [unreadTaskIds,   setUnreadTaskIds]  = useState(new Set())
  const [calSelectedDate, setCalSelectedDate] = useState(null)
  const [filterText,      setFilterText]      = useState('')
  const [filterPriority,  setFilterPriority]  = useState('')

  const profileMap = useMemo(() => Object.fromEntries(allProfiles.map(p => [p.id, p])), [allProfiles])
  const projectMap = useMemo(() => Object.fromEntries(allProjects.map(p => [p.id, p])), [allProjects])
  const getProjectReg = (projectId) => {
    if (!projectId) return null
    const p = projectMap[projectId]
    return p?.aircraft?.registration || p?.secondary_registration || null
  }

  const filteredMyTasks = useMemo(() => {
    if (!filterText && !filterPriority) return myTasks
    return myTasks.filter(t => {
      if (filterPriority && t.priority !== filterPriority) return false
      if (filterText) {
        const q = filterText.toLowerCase()
        const p = projectMap[t.linked_project_id]
        const reg = (p?.aircraft?.registration || p?.secondary_registration || '').toLowerCase()
        const assignees = (t.task_assignees || [])
          .map(a => (profileMap[a.user_id]?.display_name || profileMap[a.user_id]?.email || '').toLowerCase())
          .join(' ')
        return t.title.toLowerCase().includes(q) || reg.includes(q) || assignees.includes(q)
      }
      return true
    })
  }, [myTasks, filterText, filterPriority, projectMap, profileMap])

  const filteredAssignedTasks = useMemo(() => {
    if (!filterText && !filterPriority) return assignedTasks
    return assignedTasks.filter(t => {
      if (filterPriority && t.priority !== filterPriority) return false
      if (filterText) {
        const q = filterText.toLowerCase()
        const p = projectMap[t.linked_project_id]
        const reg = (p?.aircraft?.registration || p?.secondary_registration || '').toLowerCase()
        const assignees = (t.task_assignees || [])
          .map(a => (profileMap[a.user_id]?.display_name || profileMap[a.user_id]?.email || '').toLowerCase())
          .join(' ')
        return t.title.toLowerCase().includes(q) || reg.includes(q) || assignees.includes(q)
      }
      return true
    })
  }, [assignedTasks, filterText, filterPriority, projectMap, profileMap])

  const calMyTasks = useMemo(() => expandRecurringForCalendar(
    filteredMyTasks.map(t => ({ ...t, _isDone: isTaskDone(t.myRow?.status, t.needs_approval) }))
  ), [filteredMyTasks])

  const calAssignedTasks = useMemo(() => expandRecurringForCalendar(
    filteredAssignedTasks.map(t => {
      const assignees = t.task_assignees ?? []
      return { ...t, _isDone: assignees.length > 0 && assignees.every(a => a.status === 'approved' || a.status === 'complete') }
    })
  ), [filteredAssignedTasks])

  const assignableProfiles = useMemo(() => {
    const others = allProfiles.filter(p => p.id !== user?.id)
    if (isEditor) return others
    if (isSubtaskEditor) return others.filter(p => p.role === 'project_lead' || p.role === 'inspector')
    return []
  }, [allProfiles, user?.id, isEditor, isSubtaskEditor])

  const load = async () => {
    if (!user) return
    const [mine, assigned, unreadIds] = await Promise.all([
      fetchMyTasks(user.id),
      canAssign ? fetchAssignedTasks(user.id) : Promise.resolve([]),
      fetchUnreadCommentTaskIds(user.id),
    ])
    setMyTasks(mine)
    setAssignedTasks(assigned)
    setUnreadTaskIds(unreadIds)
  }

  const handleOpenTask = (taskId) => {
    setSelectedTaskId(taskId)
    if (unreadTaskIds.has(taskId)) {
      setUnreadTaskIds(prev => { const next = new Set(prev); next.delete(taskId); return next })
      supabase
        .from('task_notifications')
        .update({ seen_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('task_id', taskId)
        .eq('type', 'comment')
        .is('seen_at', null)
        .then(() => {})
    }
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([
      supabase.from('profiles').select('id, display_name, email, role').eq('org_id', profile?.org_id).then(r => {
        const lastName = (n) => { const p = (n || '').trim().split(/\s+/); return (p.length > 1 ? p[p.length - 1] : p[0] || '').toLowerCase() }
        const sorted = (r.data ?? []).sort((a, b) => lastName(a.display_name || a.email).localeCompare(lastName(b.display_name || b.email)))
        setAllProfiles(sorted)
      }),
      supabase.from('projects').select('id, secondary_registration, work_order, arrival_date, departure_date, status, workscope, aircraft(registration)').is('deleted_at', null).in('status', ['TBD', 'Scheduled', 'In Work']).then(r => {
        const rows = r.data ?? []
        rows.sort((a, b) => {
          const g = x => x.status === 'In Work' ? 0 : 1
          if (g(a) !== g(b)) return g(a) - g(b)
          if (!a.departure_date && !b.departure_date) return 0
          if (!a.departure_date) return 1
          if (!b.departure_date) return -1
          return a.departure_date.localeCompare(b.departure_date)
        })
        setAllProjects(rows)
      }),
      load(),
    ]).finally(() => setLoading(false))
  }, [user?.id, profile?.org_id])

  useEffect(() => {
    if (refreshKey > 0) load()
  }, [refreshKey])

  // Group my tasks by timeframe
  const myGrouped = useMemo(() => {
    const t = today()
    const nextWeek = new Date(t); nextWeek.setDate(nextWeek.getDate() + 7)
    const done = [], overdue = [], thisWeek = [], upcoming = [], noDate = []
    for (const task of filteredMyTasks) {
      const st = task.myRow?.status
      if (isTaskDone(st, task.needs_approval)) { done.push(task); continue }
      if (!task.due_date) { noDate.push(task); continue }
      const due = new Date(task.due_date + 'T00:00:00')
      if (due < t) overdue.push(task)
      else if (due < nextWeek) thisWeek.push(task)
      else upcoming.push(task)
    }
    return { overdue, thisWeek, upcoming, noDate, done }
  }, [filteredMyTasks])

  // Group assigned tasks by state
  const assignedGrouped = useMemo(() => {
    const pendingApproval = [], inProgress = [], done = []
    for (const task of filteredAssignedTasks) {
      const assignees = task.task_assignees ?? []
      const hasSubmitted = assignees.some(a => a.status === 'submitted')
      const allDone = assignees.length > 0 && assignees.every(a => a.status === 'approved' || a.status === 'complete')
      if (allDone) done.push(task)
      else if (hasSubmitted) pendingApproval.push(task)
      else inProgress.push(task)
    }
    return { pendingApproval, inProgress, done }
  }, [filteredAssignedTasks])

  const totalPendingApproval = assignedGrouped.pendingApproval.length

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => { setActiveTab('mine'); setCalSelectedDate(null) }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'mine' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            My Tasks
            {myGrouped.overdue.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-xs">
                {myGrouped.overdue.length}
              </span>
            )}
          </button>
          {canAssign && (
            <button
              onClick={() => { setActiveTab('assigned'); setCalSelectedDate(null) }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'assigned' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Assigned Tasks
              {totalPendingApproval > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand-red text-white text-xs">{totalPendingApproval}</span>
              )}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSelfCreate(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            Add To-Do
          </button>
          {canAssign && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 text-sm font-medium text-white bg-brand-red hover:bg-brand-red-dark rounded-lg px-4 py-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Task
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      {!loading && (myTasks.length > 0 || assignedTasks.length > 0) && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={filterText}
              onChange={e => { setFilterText(e.target.value); setCalSelectedDate(null) }}
              placeholder="Search by title or registration…"
              className="w-full text-sm border border-gray-200 rounded-lg pl-8 pr-7 py-2 focus:outline-none focus:border-brand-red"
            />
            {filterText && (
              <button onClick={() => setFilterText('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-base leading-none">&times;</button>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {[['', 'All'], ['high', 'High'], ['medium', 'Med'], ['low', 'Low']].map(([val, label]) => (
              <button
                key={val || 'all'}
                onClick={() => setFilterPriority(val)}
                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors ${
                  filterPriority === val
                    ? val === 'high'   ? 'bg-red-500 text-white'
                    : val === 'medium' ? 'bg-yellow-500 text-white'
                    : val === 'low'    ? 'bg-green-600 text-white'
                    :                    'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : activeTab === 'mine' ? (
        <div className="space-y-4">
          <MonthCalendar tasks={calMyTasks} selectedDate={calSelectedDate} onSelectDate={setCalSelectedDate} />

          {calSelectedDate ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {new Date(calSelectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
                <button onClick={() => setCalSelectedDate(null)} className="text-xs text-gray-400 hover:text-gray-700 transition-colors">Show all</button>
              </div>
              {calMyTasks.filter(t => t.due_date === calSelectedDate).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No tasks due on this day.</p>
              ) : (
                <div className="space-y-2">
                  {calMyTasks.filter(t => t.due_date === calSelectedDate).map(t => (
                    <TaskCard key={t.id} task={t} myRow={t.myRow} onClick={() => handleOpenTask(t._taskId || t.id)} hasUnread={unreadTaskIds.has(t._taskId || t.id)} creatorName={profileMap[t.created_by]?.display_name || profileMap[t.created_by]?.email} reg={getProjectReg(t.linked_project_id)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <SectionGroup label="Overdue" count={myGrouped.overdue.length} accentCls="text-red-600">
                {myGrouped.overdue.map(t => (
                  <TaskCard key={t.id} task={t} myRow={t.myRow} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} creatorName={profileMap[t.created_by]?.display_name || profileMap[t.created_by]?.email} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              <SectionGroup label="Due this week" count={myGrouped.thisWeek.length} accentCls="text-orange-600">
                {myGrouped.thisWeek.map(t => (
                  <TaskCard key={t.id} task={t} myRow={t.myRow} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} creatorName={profileMap[t.created_by]?.display_name || profileMap[t.created_by]?.email} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              <SectionGroup label="Upcoming" count={myGrouped.upcoming.length}>
                {myGrouped.upcoming.map(t => (
                  <TaskCard key={t.id} task={t} myRow={t.myRow} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} creatorName={profileMap[t.created_by]?.display_name || profileMap[t.created_by]?.email} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              <SectionGroup label="No due date" count={myGrouped.noDate.length}>
                {myGrouped.noDate.map(t => (
                  <TaskCard key={t.id} task={t} myRow={t.myRow} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} creatorName={profileMap[t.created_by]?.display_name || profileMap[t.created_by]?.email} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              <SectionGroup label="Completed" count={myGrouped.done.length} defaultOpen={false}>
                {myGrouped.done.map(t => (
                  <TaskCard key={t.id} task={t} myRow={t.myRow} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} creatorName={profileMap[t.created_by]?.display_name || profileMap[t.created_by]?.email} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              {myTasks.length === 0 && (
                <div className="py-16 text-center text-gray-400 text-sm">No tasks assigned to you yet.</div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <MonthCalendar tasks={calAssignedTasks} selectedDate={calSelectedDate} onSelectDate={setCalSelectedDate} />

          {calSelectedDate ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {new Date(calSelectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
                <button onClick={() => setCalSelectedDate(null)} className="text-xs text-gray-400 hover:text-gray-700 transition-colors">Show all</button>
              </div>
              {calAssignedTasks.filter(t => t.due_date === calSelectedDate).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No tasks due on this day.</p>
              ) : (
                <div className="space-y-2">
                  {calAssignedTasks.filter(t => t.due_date === calSelectedDate).map(t => (
                    <AssignedTaskCard key={t.id} task={t} onClick={() => handleOpenTask(t._taskId || t.id)} hasUnread={unreadTaskIds.has(t._taskId || t.id)} profileMap={profileMap} reg={getProjectReg(t.linked_project_id)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <SectionGroup label="Pending Your Approval" count={assignedGrouped.pendingApproval.length} accentCls="text-brand-red">
                {assignedGrouped.pendingApproval.map(t => (
                  <AssignedTaskCard key={t.id} task={t} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} profileMap={profileMap} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              <SectionGroup label="In Progress" count={assignedGrouped.inProgress.length}>
                {assignedGrouped.inProgress.map(t => (
                  <AssignedTaskCard key={t.id} task={t} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} profileMap={profileMap} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              <SectionGroup label="Done" count={assignedGrouped.done.length} defaultOpen={false}>
                {assignedGrouped.done.map(t => (
                  <AssignedTaskCard key={t.id} task={t} onClick={() => handleOpenTask(t.id)} hasUnread={unreadTaskIds.has(t.id)} profileMap={profileMap} reg={getProjectReg(t.linked_project_id)} />
                ))}
              </SectionGroup>
              {assignedTasks.length === 0 && (
                <div className="py-16 text-center text-gray-400 text-sm">You haven't assigned any tasks yet.</div>
              )}
            </>
          )}
        </div>
      )}

      {selectedTaskId && (
        <TaskDetailPanel
          taskId={selectedTaskId}
          currentUserId={user.id}
          onClose={() => setSelectedTaskId(null)}
          onRefresh={load}
          allProjects={allProjects}
          assignableProfiles={assignableProfiles}
        />
      )}

      {showCreate && (
        <TaskCreateModal
          currentUserId={user.id}
          allProfiles={allProfiles}
          assignableProfiles={assignableProfiles}
          allProjects={allProjects}
          onClose={() => setShowCreate(false)}
          onCreated={load}
        />
      )}

      {showSelfCreate && (
        <TaskCreateModal
          currentUserId={user.id}
          allProfiles={allProfiles}
          assignableProfiles={assignableProfiles}
          allProjects={allProjects}
          onClose={() => setShowSelfCreate(false)}
          onCreated={load}
          selfAssign
        />
      )}
    </div>
  )
}
